import { getTenantWebsiteUrl } from "@/app/config/tenant-runtime";
import type { TenantKeyT } from "@/app/config/tenant-registry";

/**
 * Safely get window.location.origin
 * Returns null if window or window.location is unavailable (e.g., in iframe, SSR, or worker contexts)
 */
export const getWindowOrigin = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  if (!window.location) {
    return null;
  }
  return window.location.origin || null;
};

/**
 * Safely get window.location.href
 * Returns null if window or window.location is unavailable, or if access is denied (SecurityError)
 * Handles cases where access to window.location.href is blocked (e.g., in iframe, sandbox contexts)
 */
export const getWindowLocationHref = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  if (!window.location) {
    return null;
  }
  try {
    return window.location.href || null;
  } catch {
    // SecurityError catch - access to location.href is blocked
    return null;
  }
};

/**
 * Get the base URL based on environment and current hostname
 * Supports both main domain and app subdomain
 */
export const getBaseUrl = (hostname?: string) => {
  const env = process.env.NODE_ENV;

  // Server-side or window.location unavailable: use provided hostname or fallback
  if (typeof window === "undefined" || !window.location) {
    if (hostname) {
      const protocol = env === "production" ? "https" : "http";
      return `${protocol}://${hostname}`;
    }
    // Fallback for server-side without hostname
    return env === "production"
      ? "https://wiest-group.de"
      : "http://localhost:3000";
  }

  // Client-side: use window.location
  return `${window.location.protocol}//${window.location.host}`;
};

/**
 * Get base URL for specific subdomain
 */
export const getSubdomainUrl = (subdomain?: "app") => {
  const env = process.env.NODE_ENV;

  if (env === "production") {
    return subdomain
      ? `https://${subdomain}.wiest-group.de`
      : "https://wiest-group.de";
  }

  // In development, you might want to simulate subdomains or use different ports
  return "http://localhost:3000";
};

// Helper function to generate public frontend URLs
export const getPublicFrontendUrl = (
  backendRoute: string,
  slug: string
): string | null => {
  const routeMapping: Record<string, string> = {
    "/angebote/": "/angebote",
    "/beitraege/": "/beitraege",
    "/aktionen/": "/aktionen",
    "/mitarbeiter/": "/ansprechpartner",
  };

  const matchedRoute = Object.keys(routeMapping).find((route) =>
    backendRoute.includes(route)
  );

  if (matchedRoute) {
    const publicRoute = routeMapping[matchedRoute];
    // Use the getBaseUrl helper to handle server/client side properly
    const currentBaseUrl = getBaseUrl();
    // Remove 'app.' subdomain if present to get main domain
    const baseUrl = currentBaseUrl.replace("app.", "");
    return `${baseUrl}${publicRoute}/${slug}`;
  }

  return null;
};

const JOB_TENANT_KEYS: readonly TenantKeyT[] = [
  "wiest-group",
  "autohaus-rauch",
  "schuetz-gross-umstadt",
];

const normalizeJobTenantKey = (
  tenantKey?: string | null
): TenantKeyT | null => {
  if (!tenantKey) return null;
  return JOB_TENANT_KEYS.includes(tenantKey as TenantKeyT)
    ? (tenantKey as TenantKeyT)
    : null;
};

const joinBasePath = (basePath: string, path: string): string => {
  const normalizedBasePath = basePath.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return normalizedBasePath
    ? `${normalizedBasePath}${normalizedPath}`
    : normalizedPath;
};

const getJobPreviewUrl = (slug: string, tenantKey?: string | null): string => {
  const normalizedTenantKey = normalizeJobTenantKey(tenantKey);

  if (normalizedTenantKey) {
    try {
      const tenantBaseUrl = new URL(getTenantWebsiteUrl(normalizedTenantKey));
      const targetPath = joinBasePath(
        tenantBaseUrl.pathname,
        `/karriere/${slug}`
      );
      return `${tenantBaseUrl.origin}${targetPath}`;
    } catch {
      // Fallback to current host mapping when tenant URL is invalid.
    }
  }

  const currentBaseUrl = getBaseUrl().replace("app.", "");
  return `${currentBaseUrl}/karriere/${slug}`;
};

// Function to determine the preview URL based on current route
export const getPreviewUrl = ({
  slug,
  pathname,
  companySlug,
  tenantKey,
}: {
  slug: string | undefined;
  pathname: string;
  companySlug?: string | null;
  tenantKey?: string | null;
}) => {
  if (!slug) return null;
  if (pathname.includes("/dashboard/jobs")) {
    return getJobPreviewUrl(slug, tenantKey);
  }
  if (companySlug && pathname.includes("/dashboard/angebote")) {
    const currentBaseUrl = getBaseUrl();
    const baseUrl = currentBaseUrl.replace("app.", "");
    return `${baseUrl}/firmen/${companySlug}/${slug}`;
  }
  return getPublicFrontendUrl(pathname, slug);
};
